import { useCallback, useEffect, useMemo, useRef } from 'react';

import { type EditorPresetProps } from '@atlassian/conversation-assistant-content-renderer/controllers/editor';
import { type StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { extractPlainTextFromAdf } from '../../common/utils/content-features/extractPlainTextFromAdf';

import { getAutocompleteContextMessages } from './getAutocompleteContextMessages';

export type EditorAutocompleteContext = NonNullable<EditorPresetProps['autocomplete']>;

/**
 * Stable autocomplete bridge for the editor preset: `getContext` reads the
 * latest chat messages lazily; subscribers are notified only when the eligible
 * context changes (keyed by message ids, so streaming updates don't thrash).
 */
export const useAutocompleteEditorContext = (
	storeMessages: StoreChatItem[],
): EditorAutocompleteContext => {
	const { key, messages } = useMemo(
		() => getAutocompleteContextMessages(storeMessages),
		[storeMessages],
	);

	const messagesRef = useRef(messages);
	messagesRef.current = messages;
	const listenersRef = useRef(new Set<() => void>());

	const getContext = useCallback(async () => {
		const siblingCommentsContents: string[] = [];

		for (const { content } of messagesRef.current) {
			const messageContent = (
				typeof content === 'string' ? content : extractPlainTextFromAdf(content)
			).trim();

			if (messageContent) {
				siblingCommentsContents.push(messageContent);
			}
		}

		if (siblingCommentsContents.length === 0) {
			return undefined;
		}

		return { siblingCommentsContents };
	}, []);

	const subscribeToContextUpdates = useCallback((listener: () => void) => {
		listenersRef.current.add(listener);

		return () => {
			listenersRef.current.delete(listener);
		};
	}, []);

	useEffect(() => {
		// Empty key = no eligible context yet; skip notify to avoid a spurious getContext().
		if (!key) {
			return;
		}
		listenersRef.current.forEach((listener) => {
			listener();
		});
	}, [key]);

	useEffect(() => {
		const listeners = listenersRef.current;

		return () => {
			listeners.clear();
		};
	}, []);

	return useMemo(
		() => ({ getContext, subscribeToContextUpdates }),
		[getContext, subscribeToContextUpdates],
	);
};
